import React from "react";
import {
  IonButton,
  IonButtons,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
  useIonAlert,
  useIonToast,
} from "@ionic/react";
import {
  arrowBack,
  call,
  close,
  copy,
  download,
  mail,
  trash,
} from "ionicons/icons";
import { IReservationContact } from "./useContactList";

type Props = {
  isOpen: boolean;
  close: () => void;
  contacts: IReservationContact[];
  remove: (contact: IReservationContact) => void;
  clear: () => void;
};

const HEADER = ["Ime i prezime", "Email", "Telefon", "Mesto", "Motivi", "Porudžbine"];

const row = (c: IReservationContact) => [
  c.name,
  c.email,
  c.phone,
  c.place,
  c.motifs.join(", "),
  c.cartIds.map((id) => 5000 + id).join(", "),
];

const unique = (values: string[]) =>
  Array.from(new Set(values.map((v) => v.trim()).filter((v) => v)));

// Excel with Serbian regional settings splits CSV on ";"; the BOM keeps š, č, ć, ž, đ intact.
const toCsv = (contacts: IReservationContact[]) => {
  const cell = (v: string) => `"${(v || "").replace(/"/g, '""')}"`;
  return (
    "﻿" +
    [HEADER, ...contacts.map(row)].map((r) => r.map(cell).join(";")).join("\r\n")
  );
};

// Tab-separated pastes straight into Excel / Google Sheets columns.
const toTsv = (contacts: IReservationContact[]) =>
  [HEADER, ...contacts.map(row)]
    .map((r) => r.map((v) => (v || "").replace(/[\t\r\n]+/g, " ")).join("\t"))
    .join("\n");

const ContactList = ({ isOpen, close: onClose, contacts, remove, clear }: Props) => {
  const [presentToast] = useIonToast();
  const [presentAlert] = useIonAlert();

  const emails = unique(contacts.map((c) => c.email));
  const phones = unique(contacts.map((c) => c.phone));

  const copyText = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      presentToast({ message: `Kopirano: ${what}`, duration: 1500, position: "top" });
    } catch {
      presentToast({ message: "Kopiranje nije uspelo", duration: 2000, color: "danger" });
    }
  };

  const downloadCsv = () => {
    const blob = new Blob([toCsv(contacts)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rezervacije_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const askClear = () =>
    presentAlert({
      header: "Očisti listu",
      message: `Ukloniti svih ${contacts.length} kontakata sa liste?`,
      buttons: [
        { text: "Odustani", role: "cancel" },
        { text: "Očisti", role: "destructive", handler: clear },
      ],
    });

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar color="danger">
          <IonButtons slot="start">
            <IonIcon
              className="f28"
              icon={arrowBack}
              onClick={onClose}
              style={{ paddingLeft: "5px", cursor: "pointer" }}
            />
          </IonButtons>
          <IonTitle>Lista kontakata ({contacts.length})</IonTitle>
        </IonToolbar>
      </IonHeader>
      {/* A plain scrolling div, as in the other modals: IonContent inside IonModal renders empty. */}
      <div className="ion-padding" style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          <IonButton
            size="small"
            disabled={emails.length === 0}
            onClick={() => copyText(emails.join(", "), `${emails.length} email adresa`)}
          >
            <IonIcon slot="start" icon={mail} />
            Email adrese ({emails.length})
          </IonButton>
          <IonButton
            size="small"
            disabled={phones.length === 0}
            onClick={() => copyText(phones.join("\n"), `${phones.length} telefona`)}
          >
            <IonIcon slot="start" icon={call} />
            Telefoni ({phones.length})
          </IonButton>
          <IonButton
            size="small"
            color="medium"
            disabled={contacts.length === 0}
            onClick={() => copyText(toTsv(contacts), "tabela")}
          >
            <IonIcon slot="start" icon={copy} />
            Tabela
          </IonButton>
          <IonButton
            size="small"
            color="success"
            disabled={contacts.length === 0}
            onClick={downloadCsv}
          >
            <IonIcon slot="start" icon={download} />
            CSV
          </IonButton>
          <IonButton
            size="small"
            color="danger"
            fill="outline"
            disabled={contacts.length === 0}
            onClick={askClear}
          >
            <IonIcon slot="start" icon={trash} />
            Očisti listu
          </IonButton>
        </div>
        <div className="f13" style={{ margin: "8px 0", opacity: 0.7 }}>
          Email adrese se kopiraju razdvojene zarezom (za BCC polje), telefoni jedan ispod
          drugog, a tabela se lepi direktno u Excel.
        </div>

        {contacts.length === 0 ? (
          <div className="ion-text-center" style={{ padding: 30, opacity: 0.7 }}>
            Lista je prazna. Dodajte kupce sa stranice Rezervacije.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              className="f13"
              style={{ width: "100%", borderCollapse: "collapse", userSelect: "text" }}
            >
              <thead>
                <tr style={{ textAlign: "left" }}>
                  {["Ime i prezime", "Email", "Telefon", "Motivi", ""].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "6px 4px",
                        borderBottom: "2px solid var(--ion-color-medium)",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr
                    key={c.cartIds.join("-")}
                    style={{ borderBottom: "1px solid var(--ion-color-light-shade)" }}
                  >
                    <td style={{ padding: "6px 4px" }}>
                      <div className="b">{c.name}</div>
                      <div style={{ opacity: 0.7 }}>{c.place}</div>
                    </td>
                    <td style={{ padding: "6px 4px", wordBreak: "break-all" }}>{c.email}</td>
                    <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>{c.phone}</td>
                    <td style={{ padding: "6px 4px" }}>{c.motifs.join(", ")}</td>
                    <td style={{ padding: "6px 0", width: 32 }}>
                      <IonIcon
                        icon={close}
                        className="cursorP"
                        style={{ fontSize: 20 }}
                        aria-label={`Ukloni ${c.name}`}
                        onClick={() => remove(c)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </IonModal>
  );
};

export default ContactList;
